/**
 * AI Magic Interview — QuestionSet schema.
 *
 * A `QuestionSet` is one atomic clarification round the assistant asks the
 * user. It replaces the earlier single-answer clarification flow: instead of
 * emitting one question at a time (and often abusing `radio` for what should
 * be multi-select), the assistant emits a small SET of questions that share a
 * theme, each with its own semantically-correct input type. The composer then
 * renders the set as a single premium card and returns a structured envelope
 * on submit so the assistant can act deterministically on the next turn.
 *
 * Design invariants (see conversation notes):
 *   - RADIO is used only when options are mutually exclusive.
 *   - CHECKBOX is used whenever multiple selections make sense.
 *   - TEXT / DATE / DATE_RANGE / NUMBER / SLIDER are used when the natural
 *     answer isn't a discrete option pick.
 *   - ATTACHMENT_INVENTORY is client-authored after attachment extraction;
 *     model responses cannot populate it.
 *   - The client always accepts a free-text override for any question type;
 *     the assistant re-interprets ("first week of November") on the next turn.
 *   - Legacy `quickControls` turns keep rendering through the old path unless
 *     they're auto-upgraded to a synthesized QuestionSet by grouping.
 */

export type AiMagicQuestionType =
  | 'RADIO'
  | 'CHECKBOX'
  | 'TEXT'
  | 'DATE'
  | 'DATE_RANGE'
  | 'NUMBER'
  | 'SLIDER'
  | 'ATTACHMENT_INVENTORY'

export interface AiMagicQuestionOption {
  label: string
  value: string
  /** Optional short helper shown under the label. */
  hint?: string
}

export interface AiMagicQuestion {
  /** Stable id used as the payload key on submit. */
  id: string
  /** Human-readable prompt shown above the input. */
  prompt: string
  type: AiMagicQuestionType
  /** For RADIO / CHECKBOX. Ignored for other types. */
  options?: AiMagicQuestionOption[]
  /** When true, Send answers stays disabled until this question has a value. */
  required?: boolean
  /** Numeric bounds for NUMBER / SLIDER. */
  min?: number
  max?: number
  step?: number
  /** Placeholder for TEXT / DATE / NUMBER inputs. */
  placeholder?: string
  /** Optional pre-fill; string for single-value types, string[] for CHECKBOX. */
  default?: string | string[]
  /** One-line hint rendered below the input. */
  helperText?: string
  /** Client-authored, source-grouped attachment curation payload. */
  attachmentInventory?: import('../attachment/attachmentPlanningIntelligence').AttachmentInventory
}

export interface AiMagicQuestionSet {
  /** Stable id used to correlate the submission envelope with this ask. */
  id: string
  /** Optional short title shown at the top of the card. */
  title?: string
  /** When false, the Skip button is hidden. Default true. */
  skippable?: boolean
  questions: AiMagicQuestion[]
}

const KNOWN_TYPES: readonly AiMagicQuestionType[] = [
  'RADIO', 'CHECKBOX', 'TEXT', 'DATE', 'DATE_RANGE', 'NUMBER', 'SLIDER',
]

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined

const asBoolean = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined

const normalizeOptions = (value: unknown): AiMagicQuestionOption[] => {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  const out: AiMagicQuestionOption[] = []
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue
    const record = raw as Record<string, unknown>
    const label = asString(record.label)
    const rawValue = asString(record.value) ?? label
    if (!label || !rawValue) continue
    if (seen.has(rawValue)) continue
    seen.add(rawValue)
    const hint = asString(record.hint)
    out.push(hint ? { label, value: rawValue, hint } : { label, value: rawValue })
    if (out.length >= 8) break
  }
  return out
}

const normalizeQuestion = (raw: unknown, fallbackIndex: number): AiMagicQuestion | null => {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const prompt = asString(record.prompt) ?? asString(record.label)
  if (!prompt) return null
  const upperType = typeof record.type === 'string' ? record.type.toUpperCase() : ''
  const type = (KNOWN_TYPES as readonly string[]).includes(upperType)
    ? (upperType as AiMagicQuestionType)
    : 'TEXT'
  const options = type === 'RADIO' || type === 'CHECKBOX' ? normalizeOptions(record.options) : undefined
  if ((type === 'RADIO' || type === 'CHECKBOX') && (!options || options.length === 0)) {
    return null
  }
  const id = asString(record.id) ?? `q${fallbackIndex + 1}`
  const question: AiMagicQuestion = { id, prompt, type }
  if (options) question.options = options
  const required = asBoolean(record.required)
  if (required !== undefined) question.required = required
  const min = asNumber(record.min); if (min !== undefined) question.min = min
  const max = asNumber(record.max); if (max !== undefined) question.max = max
  const step = asNumber(record.step); if (step !== undefined) question.step = step
  const placeholder = asString(record.placeholder); if (placeholder) question.placeholder = placeholder
  const helperText = asString(record.helperText); if (helperText) question.helperText = helperText
  if (type === 'CHECKBOX') {
    if (Array.isArray(record.default)) {
      const values = record.default.filter((entry): entry is string => typeof entry === 'string')
      if (values.length > 0) question.default = values
    } else {
      const single = asString(record.default)
      if (single) question.default = [single]
    }
  } else {
    const single = asString(record.default)
    if (single) question.default = single
  }
  return question
}

/**
 * Validate and coerce an unknown payload into a QuestionSet, or return null if
 * the input is unusable. Preserves all fields the client understands; drops
 * malformed entries silently rather than throwing.
 */
export const normalizeQuestionSet = (value: unknown): AiMagicQuestionSet | null => {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const rawQuestions = Array.isArray(record.questions) ? record.questions : null
  if (!rawQuestions || rawQuestions.length === 0) return null
  const questions: AiMagicQuestion[] = []
  const usedIds = new Set<string>()
  for (let i = 0; i < rawQuestions.length && questions.length < 6; i += 1) {
    const q = normalizeQuestion(rawQuestions[i], i)
    if (!q) continue
    let id = q.id
    if (usedIds.has(id)) id = `${id}-${questions.length + 1}`
    usedIds.add(id)
    questions.push({ ...q, id })
  }
  if (questions.length === 0) return null
  const id = asString(record.id) ?? `set-${Date.now().toString(36)}`
  const title = asString(record.title)
  const skippable = asBoolean(record.skippable)
  const set: AiMagicQuestionSet = { id, questions }
  if (title) set.title = title
  if (skippable !== undefined) set.skippable = skippable
  return set
}

/**
 * True when a QuestionSet has every required question answered. Answers are
 * keyed by question id; string for single-value types, string[] for CHECKBOX.
 */
export const isQuestionSetReady = (
  set: AiMagicQuestionSet,
  answers: Record<string, string | string[] | undefined>,
): boolean => {
  for (const question of set.questions) {
    if (!question.required) continue
    const answer = answers[question.id]
    if (question.type === 'CHECKBOX' || question.type === 'ATTACHMENT_INVENTORY') {
      if (!Array.isArray(answer) || answer.length === 0) return false
    } else {
      if (typeof answer !== 'string' || answer.trim().length === 0) return false
    }
  }
  return true
}
